import React, { useEffect, useMemo, useRef, useState } from "react";
import { Formik, Form, useFormikContext } from "formik";
import * as Yup from "yup";
import { useDispatch, useSelector } from "react-redux";
import { PfInput, PfMasked, PfDate, MASKS } from "./ProposalFormFields";
import { isAtLeastAge } from "./ProposalFormFields";
import { updateTravellerDetails } from "../redux/slices/TravellerDetailsSlice";
import { SecureField, maskName, maskEmail, maskPan, maskPassport } from "./maskUtils";

const fullNamePattern = /^[A-Za-z]{2,}(?:\s+[A-Za-z]{1,})+$/;
const passportPattern = /^[A-Za-z0-9]{6,20}$/;

const occupationOptions = [
  { label: "Service", value: "Service" },
  { label: "Business", value: "Business" },
  { label: "Self Employed", value: "Self Employed" },
  { label: "Homemaker", value: "Homemaker" },
  { label: "Student", value: "Student" },
  { label: "Retired", value: "Retired" },
];

const visaTypeOptions = [
  { label: "Tourist", value: "Tourist" },
  { label: "Business", value: "Business" },
  { label: "Student", value: "Student" },
];

const genderOptions = [
  { label: "Male", value: "Male" },
  { label: "Female", value: "Female" },
  { label: "Other", value: "Other" },
];

const relationOptions = [
  { label: "Spouse", value: "Spouse" },
  { label: "Father", value: "Father" },
  { label: "Mother", value: "Mother" },
  { label: "Son", value: "Son" },
  { label: "Daughter", value: "Daughter" },
  { label: "Brother", value: "Brother" },
  { label: "Sister", value: "Sister" },
  { label: "Other", value: "Other" },
];

const normalizeSpaces = (value) =>
  String(value || "")
    .replace(/\s+/g, " ")
    .replace(/^\s+/, "");

const toTitleCase = (value) =>
  normalizeSpaces(value)
    .toLowerCase()
    .replace(/\b[a-z]/g, (ch) => ch.toUpperCase());

const getSalutationFromGender = (gender) => {
  const next = String(gender || "").trim().toLowerCase();
  if (next === "male" || next === "m") return "Mr";
  if (next === "female" || next === "f") return "Ms";
  return "";
};

const CustomSelect = ({
  label,
  required,
  error,
  value,
  placeholder = "Select",
  options = [],
  onChange,
  onBlur,
}) => {
  const [open, setOpen] = useState(false);
  const wrapRef = useRef(null);

  const selectedLabel = useMemo(() => {
    const match = options.find((opt) => String(opt.value) === String(value));
    return match?.label || placeholder;
  }, [options, value, placeholder]);

  useEffect(() => {
    if (!open) return undefined;
    const handleDown = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) {
        setOpen(false);
        onBlur?.();
      }
    };
    document.addEventListener("mousedown", handleDown);
    return () => document.removeEventListener("mousedown", handleDown);
  }, [open, onBlur]);

  return (
    <div className={`pf-field${open ? " pf-field--open" : ""}`} ref={wrapRef}>
      {label && (
        <label className="pf-label">
          {label}
          {required && <span className="pf-required">*</span>}
        </label>
      )}
      <button
        type="button"
        className={`pf-select pf-select--custom${error ? " has-error" : ""}${
          value ? " has-value" : ""
        }${open ? " is-open" : ""}`}
        aria-expanded={open}
        aria-haspopup="listbox"
        onClick={() => setOpen((prev) => !prev)}
      >
        <span className="pf-select-value">{selectedLabel}</span>
        <span className={`pf-select-caret${open ? " is-open" : ""}`}>▾</span>
      </button>
      {open && (
        <div className="pf-select-menu" role="listbox">
          {options.map((opt) => (
            <button
              key={opt.value}
              type="button"
              className={`pf-select-item${
                String(opt.value) === String(value) ? " is-selected" : ""
              }`}
              onClick={() => {
                onChange?.(opt.value);
                setOpen(false);
              }}
            >
              {opt.label}
            </button>
          ))}
        </div>
      )}
      {error && <span className="pf-error">⚠ {error}</span>}
    </div>
  );
};

const memberSchema = Yup.object({
  fullName: Yup.string()
    .matches(fullNamePattern, "Enter at least first and last name")
    .required("Full name is required"),
  gender: Yup.string().required("Select gender"),
  occupation: Yup.string().required("Select occupation"),
  dob: Yup.string()
    .required("Date of birth is required")
    .test("min-age", "Member must be at least 18 years old", (value) =>
      isAtLeastAge(value, 18),
    ),
  visaType: Yup.string().required("Select visa type"),
  passportNumber: Yup.string()
    .matches(passportPattern, "Invalid passport number")
    .required("Passport number is required"),
  nomineeName: Yup.string()
    .matches(fullNamePattern, "Enter at least first and last name")
    .required("Nominee name is required"),
  nomineeRelation: Yup.string().required("Select nominee relation"),
});

const schema = Yup.object({
  members: Yup.array()
    .of(memberSchema)
    .min(1, "At least one member is required"),
});

const defaultMember = (row = {}) => ({
  ...row,
  fullName: row.fullName || "",
  salutation: row.salutation || getSalutationFromGender(row.gender),
  occupation: row.occupation || "",
  dob: row.dob || "",
  gender: row.gender || "",
  visaType: row.visaType || "",
  passportNumber: row.passportNumber || "",
  nomineeName: row.nomineeName || "",
  nomineeRelation: row.nomineeRelation || "",
});

const buildMemberDefaults = (travellerDetails) => {
  const travellers = Array.isArray(travellerDetails?.travellers)
    ? travellerDetails.travellers
    : [];
  const memberCount = Math.max(travellers.length - 1, 1);
  const savedMembers = Array.isArray(travellerDetails?.memberDetails)
    ? travellerDetails.memberDetails
    : travellerDetails?.memberDetails
      ? [travellerDetails.memberDetails]
      : [];

  return Array.from({ length: memberCount }, (_, index) =>
    defaultMember(savedMembers[index] || {}),
  );
};

const MemberDetailsFormBody = ({ onChange, onValidityChange }) => {
  const { values, errors, touched, setFieldValue, setFieldTouched, isValid } =
    useFormikContext();
  const dispatch = useDispatch();
  const travellerDetails = useSelector((state) => state.TravellerDetails);
  const lastPersistedRef = useRef("");
  const members = Array.isArray(values.members) ? values.members : [];

  useEffect(() => {
    onValidityChange?.(Boolean(isValid));
  }, [isValid, onValidityChange]);

  useEffect(() => {
    onChange?.(members);
  }, [members, onChange]);

  useEffect(() => {
    const nextSnapshot = JSON.stringify(members || []);
    if (lastPersistedRef.current === nextSnapshot) return;
    lastPersistedRef.current = nextSnapshot;
    dispatch(
      updateTravellerDetails({
        ...travellerDetails,
        memberDetails: members,
        member: members[0] || {},
        memberForm: members[0] || {},
      }),
    );
  }, [members, dispatch, travellerDetails]);

  const show = (index, field) =>
    Boolean(
      touched.members?.[index]?.[field] && errors.members?.[index]?.[field],
    );

  const val = (index, field) => values.members?.[index]?.[field] ?? "";

  const change = (index, field) => (e) => {
    const raw = typeof e === "string" ? e : e.target.value;
    const next =
      field === "fullName" || field === "nomineeName"
        ? toTitleCase(raw)
        : normalizeSpaces(raw);
    setFieldValue(`members[${index}].${field}`, next, true);
  };

  return (
    <Form>
      <div className="pg-form-section-label">Member Details</div>
      <div className="pg-member-list">
        {members.map((member, index) => (
          <div
            key={index}
            className="pg-member-card"
            style={{ marginBottom: "10px", fontWeight: "700" }}
          >
            <div className="pg-member-card-title">Member {index + 1}</div>
            <div className="pg-form-grid">
              <PfInput
                label="Full Name"
                required
                placeholder="Enter full name"
                error={
                  show(index, "fullName")
                    ? errors.members?.[index]?.fullName
                    : ""
                }
                value={val(index, "fullName")}
                onChange={change(index, "fullName")}
                onBlur={() =>
                  setFieldTouched(`members[${index}].fullName`, true)
                }
              />

              <CustomSelect
                label="Occupation"
                required
                error={
                  show(index, "occupation")
                    ? errors.members?.[index]?.occupation
                    : ""
                }
                value={val(index, "occupation")}
                placeholder="Select occupation"
                options={[
                  { label: "Select occupation", value: "" },
                  ...occupationOptions,
                ]}
                onChange={(v) =>
                  setFieldValue(`members[${index}].occupation`, v, true)
                }
                onBlur={() =>
                  setFieldTouched(`members[${index}].occupation`, true)
                }
              />

              <PfDate
                label="Date of Birth"
                required
                error={show(index, "dob") ? errors.members?.[index]?.dob : ""}
                value={val(index, "dob")}
                onChange={change(index, "dob")}
                onBlur={() => setFieldTouched(`members[${index}].dob`, true)}
                maxDate={
                  new Date(
                    new Date().getFullYear() - 18,
                    new Date().getMonth(),
                    new Date().getDate(),
                  )
                }
                allowTyping
              />
              <CustomSelect
                label="Gender"
                required
                error={
                  show(index, "gender") ? errors.members?.[index]?.gender : ""
                }
                value={val(index, "gender")}
                placeholder="Select gender"
                options={[
                  { label: "Select gender", value: "" },
                  ...genderOptions,
                ]}
                onChange={(v) => {
                  setFieldValue(`members[${index}].gender`, v, true);
                  setFieldValue(
                    `members[${index}].salutation`,
                    getSalutationFromGender(v),
                    true,
                  );
                }}
                onBlur={() => setFieldTouched(`members[${index}].gender`, true)}
              />

              <CustomSelect
                label="Visa Type"
                required
                error={
                  show(index, "visaType")
                    ? errors.members?.[index]?.visaType
                    : ""
                }
                value={val(index, "visaType")}
                placeholder="Select visa type"
                options={[
                  { label: "Select visa type", value: "" },
                  ...visaTypeOptions,
                ]}
                onChange={(v) =>
                  setFieldValue(`members[${index}].visaType`, v, true)
                }
                onBlur={() =>
                  setFieldTouched(`members[${index}].visaType`, true)
                }
              />

              {/*<PfMasked
                label="Passport Number"
                required
                mask={MASKS.passport}
                placeholder="A1234567"
                style={{ textTransform: "uppercase" }}
                error={
                  show(index, "passportNumber")
                    ? errors.members?.[index]?.passportNumber
                    : ""
                }
                value={val(index, "passportNumber")}
                onChange={change(index, "passportNumber")}
                onBlur={() =>
                  setFieldTouched(`members[${index}].passportNumber`, true)
                }
              />*/}

                    <SecureField
                        as={PfMasked}
                        maskFn={maskPassport}
                        label="Passport Number"
                        required
                        mask={MASKS.passport}
                        placeholder="A1234567"
                        style={{ textTransform: "uppercase" }}
                        error={show(index, "passportNumber") ? errors.members?.[index]?.passportNumber : ""}
                        value={val(index, "passportNumber")}
                        onChange={change(index, "passportNumber")}
                        onBlur={() => setFieldTouched(`members[${index}].passportNumber`, true)}
                    />

            </div>

            <div className="pg-form-section-label">Nominee Details</div>
                <div className="pg-form-grid">



                    {/*<PfInput
                        label="Nominee Name"
                        required
                        placeholder="Enter nominee name"
                        error={
                          show(index, "nomineeName")
                            ? errors.members?.[index]?.nomineeName
                            : ""
                        }
                        value={val(index, "nomineeName")}
                        onChange={change(index, "nomineeName")}
                        onBlur={() =>
                          setFieldTouched(`members[${index}].nomineeName`, true)
                        }
                    />*/}

                    <SecureField
                        as={PfInput}
                        maskFn={maskName}
                        label="Nominee Name"
                        required
                        placeholder="Enter nominee name"
                        error={
                            show(index, "nomineeName")
                                ? errors.members?.[index]?.nomineeName
                                : ""
                        }
                        value={val(index, "nomineeName")}
                        onChange={change(index, "nomineeName")}
                        onBlur={() =>
                            setFieldTouched(`members[${index}].nomineeName`, true)
                        }
                    />

              <CustomSelect
                label="Nominee Relation"
                required
                error={
                  show(index, "nomineeRelation")
                    ? errors.members?.[index]?.nomineeRelation
                    : ""
                }
                value={val(index, "nomineeRelation")}
                placeholder="Select relation"
                options={[
                  { label: "Select relation", value: "" },
                  ...relationOptions,
                ]}
                onChange={(v) =>
                  setFieldValue(`members[${index}].nomineeRelation`, v, true)
                }
                onBlur={() =>
                  setFieldTouched(`members[${index}].nomineeRelation`, true)
                }
              />
            </div>
          </div>
        ))}
      </div>
    </Form>
  );
};

const MemberDetailsForm = ({ onChange, onValidityChange }) => {
  const travellerDetails = useSelector((state) => state.TravellerDetails);
  const initialMemberValues = useMemo(
    () => buildMemberDefaults(travellerDetails),
    [travellerDetails],
  );

  return (
    <Formik
      initialValues={{ members: initialMemberValues }}
      validationSchema={schema}
      validateOnMount
      validateOnChange
      validateOnBlur
      enableReinitialize
      onSubmit={() => {}}
    >
      <MemberDetailsFormBody
        onChange={onChange}
        onValidityChange={onValidityChange}
      />
    </Formik>
  );
};

export default MemberDetailsForm;
